import React, { useEffect, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Clock,
  User,
  Edit,
  Trash2,
  Plus,
  CalendarDays,
  Sun,
  Moon,
  XCircle,
  Calendar,
  Briefcase,
  Palmtree,
  CheckCircle2,
  Lock,
  Search,
  Lightbulb,
  Eye,
  ChevronRight,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  getShift,
  upsertShift,
  deleteShift,
  getOTLogs,
  getEmployeeShiftSchedules,
  upsertEmployeeShiftSchedule,
  updateOvertimeStatus,
  createOTLog,
} from "@/services/attendanceService";
import {
  format,
} from "date-fns";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";
import { useSubscription } from "@/contexts/SubscriptionContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLocation, useNavigate } from "react-router-dom";
import { DatePickerInput } from "@/components/ui/DatePickerInput";
import ConfirmDialog from "@/components/ConfirmDialog";
import RequestDetailsDialog from "@/components/RequestDetailsDialog";
import { useDebounce } from "@/utils/useDebounce";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PaginationPageSize } from "@/components/ui/pagination";

const Shifts = () => {
  const location = useLocation();
  const activeParams = location.state?.tab || "shifts";
  const [activeTab, setActiveTab] = useState(activeParams);
   const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editedSchedule, setEditedSchedule] = useState(null);
  const [editShiftDialogOpen, setEditShiftDialogOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [editedShift, setEditedShift] = useState(null);
  const [showOTDialog, setShowOTDialog] = useState(false);
  const [otForm, setOTForm] = useState({
    employee_id: "",
    date: format(new Date(), "yyyy-MM-dd"),
    from_time: "",
    to_time: "",
    project: "",
    notes: "",
  });
  const defaultDayConfig = {
    isWorking: true,
    type: "full",
    specificWeeks: [],
  };
  const defaultWeekendConfig = {
    isWorking: false,
    type: "off",
    specificWeeks: [],
  };
  const defaultFormData = {
    name: "",
    startTime: "09:30",
    endTime: "18:30",
    breakDuration: "15",
    graceTime: "15",
    breakStartsAt: "13:00",
    schedule: {
      Monday: { ...defaultDayConfig },
      Tuesday: { ...defaultDayConfig },
      Wednesday: { ...defaultDayConfig },
      Thursday: { ...defaultDayConfig },
      Friday: { ...defaultDayConfig },
      Saturday: { ...defaultWeekendConfig },
      Sunday: { ...defaultWeekendConfig },
    },
  };
  const [formData, setFormData] = useState(defaultFormData);
  const [shiftData, setShiftData] = useState([]);
  const [overtimeRequests, setOvertimeRequests] = useState([]);
  const [overtimeSummary, setOvertimeSummary] = useState({
    totalCombinedOTHOurs: 0,
    pendingOTApprovals: 0,
    employeewithOT: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [employees, setEmployees] = useState([]);
  const [rotationalShifts, setRotationalShifts] = useState([]);
  const [currentWeeklyPage, setCurrentWeeklyPage] = useState(1);
  const { reloadOnboardingStatus } = useOnboarding();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmData, setConfirmData] = useState({
    title: "",
    message: "",
  });
  const [shiftToDelete, setShiftToDelete] = useState(null);
  const [weeklyPage, setWeeklyPage] = useState(1);
  const [weeklyTotalCount, setWeeklyTotalCount] = useState(0);
  const [weeklyTotalPages, setWeeklyTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [loading, setLoading] = useState(false);
  const [shiftMasterLoading, setShiftMasterLoading] = useState(false);
  const [otTimeError, setOtTimeError] = useState("");

  const { can } = usePermission();
  const { canView, canCreate, canEdit, canDelete } = createCanHelpers(can);

  const canViewShift = canView("shifts");
  const canViewSchedules = canView("rotational_shift_schedules");
  const canViewOT = canView("overtime_management");

  const canCreateShift = canCreate("shifts");
  const canCreateOT = canCreate("overtime_management");

  const canEditSchedules = canEdit("rotational_shift_schedules");
  const canEditOT = canEdit("overtime_management");
  const canEditShift = canEdit("shifts");

  const canDeleteShift = canDelete("shifts");

  const [itemsPerPage, setItemsPerPage] = useState(5);
  const handlePageSizeChange = (value) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1); // reset to first page when page size changes
  };
  // Pagination logic
  const totalItems = overtimeRequests.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const paginatedRequests = overtimeRequests.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const paginatedRotationalRequests = rotationalShifts.slice(
    (currentWeeklyPage - 1) * 10,
    currentWeeklyPage * 10
  );
  const [showAdminOTDialog, setShowAdminOTDialog] = useState(false);
  const [otViewOnly, setOtViewOnly] = useState(false);
  const [adminOTForm, setAdminOTForm] = useState({
    date: null,
    from_time: "",
    to_time: "",
    project: "",
    notes: "",
    applied_date: "",
    approved_by: null,
    approved_date: null,
    company_id: null,
    employee_id: null,
    employee_name: null,
    ot_hours: null,
    rejected_by: null,
    rejected_date: null,
    rejection_reason: "",
    status: "pending",
    _id: null,
  });
  const minuteOptions = Array.from({ length: 60 }, (_, i) =>
    i.toString().padStart(2, "0"),
  );
  const { hasFeature , isPaidOnline} = useSubscription();
  const navigate = useNavigate();
  
  const multiShiftSupport = hasFeature("multi_shift_support");
  const hasSingleShiftLimit =
  !multiShiftSupport && (shiftData?.length ?? 0) >= 1;
  const canManageOvertime = hasFeature("overtime_management");

  useEffect(() => {
    if (canViewShift) fetchShifts();
    if (canViewSchedules) fetchRotationalShifts();
    if (canManageOvertime && canViewOT) fetchovertimeRequests();
  }, []);

  useEffect(() => {
    setWeeklyPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    fetchRotationalShifts();
  }, [weeklyPage, debouncedSearch]);
 
  const fetchShifts = async () => {
    try {
      setShiftMasterLoading(true);
      const response = await getShift();
      console.log("shifts---", response.shifts);

      setShiftData(response.shifts || []);
    } catch (error) {
      console.error("Error fetching Shifts", error);
    } finally {
      setShiftMasterLoading(false);
    }
  };

  const fetchovertimeRequests = async () => {
    try {
      const response = await getOTLogs();
      const otData = response || [];
      console.log("otData", otData);

      setOvertimeRequests(otData);
      calculateOTSummary(otData);
    } catch (error) {
      console.error("Error fetching Overtime Requests", error);
      // toast.error("Failed to load Overtime Requests");
    }
  };

  const calculateOTSummary = (data) => {
    if (!Array.isArray(data)) return;

    // Total OT hours (convert to number)
    const totalCombinedOTHours = data.reduce(
      (sum, log) => sum + Number(log.ot_hours),
      0
    );

    // Pending approvals count
    const pendingOTApprovals = data.filter(
      (log) => log.status === "pending"
    ).length;

    // Employees with OT (approved or pending)
    const employeesWithOT = data.filter(
      (log) => log.status === "approved" || log.status === "pending"
    ).length;

    setOvertimeSummary({
      totalCombinedOTHOurs: totalCombinedOTHours,
      pendingOTApprovals: pendingOTApprovals,
      employeewithOT: employeesWithOT,
    });
  };

  const fetchRotationalShifts = async () => {
    try {
      setLoading(true);

      const response = await getEmployeeShiftSchedules({
        page: weeklyPage,
        limit: 10,
        search: debouncedSearch,
      });

      const { data, totalItems, totalPages } = response;

      const simplifiedEmployees = data.map((record) => ({
        name: record.name,
        _id: record._id,
        shift: record.shift,
      }));

      setEmployees(simplifiedEmployees);
      setRotationalShifts(data);

      // 🔥 Use separate states (not your global totalItems)
      setWeeklyTotalCount(totalItems);
      setWeeklyTotalPages(totalPages);
    } catch (error) {
      console.error("Error fetching shifts:", error);
    } finally {
      setLoading(false);
    }
  };
 
  const handleEditSchedule = (schedule) => {
    setSelectedEmployee(schedule.employee_id);
    console.log(schedule);

    setEditedSchedule({ ...schedule });
    setEditDialogOpen(true);
  };

  const handleShiftChange = async () => {
    if (!canEdit("rotational_shift_schedules", true)) return;
    try {
      await upsertEmployeeShiftSchedule({
        employee_id: selectedEmployee,
        shift_id: editedSchedule.shift._id,
      });
      setEditDialogOpen(false);
      fetchRotationalShifts();
      toast.success("Shift assigned to employee");
    } catch (error) {
      toast.error("Unable to assigned shift to employee");
    }
  };

  const handleEditShift = (shift) => {
    setSelectedShift(shift);
    setEditedShift({ ...shift });
    setFormData({
      name: shift.name,
      startTime: shift.startTime,
      endTime: shift.endTime,
      schedule: shift.schedule,
      breakStartsAt: shift.breakStartsAt,
      breakDuration: shift.breakDuration,
      graceTime: shift.graceTime,
    });

    setEditShiftDialogOpen(true);
  };

  const handleDeleteShift = (shift) => {
    if (!shift?._id) {
      toast.error("Shift ID is missing");
      return;
    }

    setShiftToDelete(shift);

    setConfirmData({
      title: "Delete Shift",
      message: `Are you sure you want to delete "${shift.name}"? This action cannot be undone.`,
    });

    setConfirmOpen(true);
  };

  const confirmDeleteShift = async () => {
    if (!shiftToDelete?._id) return;

    try {
      await deleteShift(shiftToDelete._id);
      await fetchShifts();
      toast.success("Shift deleted successfully");
    } catch (error) {
      console.error("Shift Delete Error:", error);
      toast.error("Failed to delete shift");
    } finally {
      setConfirmOpen(false);
      setShiftToDelete(null);
    }
  };

  const handleShiftTimingChange = (field, value) => {
    setEditedShift((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveShift = async () => {
    if (!canEdit("shifts", true)) return;
    try {
      // ---- 1) Build safe shift object (for add or edit) ----
      const shiftToSave = {
        name: formData?.name ?? "",
        startTime: formData?.startTime ?? "",
        endTime: formData?.endTime ?? "",
        schedule: formData.schedule,
        breakDuration: formData.breakDuration,
        breakStartsAt: formData.breakStartsAt,
        graceTime: formData.graceTime,
        _id: editedShift?._id || null,
      };

      // ---- 2) Validation ----
      if (!shiftToSave.name.trim()) {
        toast.error("Shift name is required");
        return;
      }

      if (!shiftToSave.startTime || !shiftToSave.endTime) {
        toast.error("Start and end time is required");
        return;
      }

      console.log("shiftToSave----------->", shiftToSave);

      await upsertShift(shiftToSave);
      await fetchShifts();
      reloadOnboardingStatus();
      setEditShiftDialogOpen(false);
      fetchRotationalShifts();
      toast.success(`Shift saved successfully`);
    } catch (error) {
      console.error("Shift Save Error:", error);
      toast.error(`Failed to save shift`);
    }
  };

  const handleOpenAddShiftModal = () => {
    setFormData(defaultFormData);
    const defaultDayConfig = {
      isWorking: true,
      type: "full",
      specificWeeks: [],
    };
    const defaultWeekendConfig = {
      isWorking: false,
      type: "off",
      specificWeeks: [],
    };

    setEditedShift({
      name: "",
      shiftStart: "09:30",
      shiftEnd: "18:30",
      schedule: {
        Monday: { ...defaultDayConfig },
        Tuesday: { ...defaultDayConfig },
        Wednesday: { ...defaultDayConfig },
        Thursday: { ...defaultDayConfig },
        Friday: { ...defaultDayConfig },
        Saturday: { ...defaultWeekendConfig },
        Sunday: { ...defaultWeekendConfig },
      },
    });
    setSelectedShift(null);
    setEditShiftDialogOpen(true);
  };

  const getWeekOfMonth = (date = new Date()) => {
    // 1..5 based on day-of-month
    const d = new Date(date);
    return Math.floor((d.getDate() - 1) / 7) + 1;
  };

const getShiftBadge = (shift, name, date = new Date()) => {
  const baseClasses =
    "transition-all duration-200 ease-in-out cursor-default hover:scale-105 hover:shadow-md";

  if (!shift)
    return (
      <Badge className={`bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-500/20 bg-gray-500/20 ${baseClasses}`}>
        --
      </Badge>
    );
  // ✅ custom logic: working only in selected weeks
  if (shift.type === "custom") {
    const weekNo = getWeekOfMonth(date);
    const specificWeeks = Array.isArray(shift.specificWeeks)
      ? shift.specificWeeks
      : [];
    const isWorkingThisWeek = specificWeeks.includes(weekNo);

    return isWorkingThisWeek ? (
      <Badge
        title={`Shift: ${name}`}
        className={`bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 ${baseClasses}`}
      >
        {name}
      </Badge>
    ) : (
      <Badge
        title="Weekly Off"
        className={`bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-500/20 hover:bg-gray-500/20 ${baseClasses}`}
      >
        Off
      </Badge>
    );
  }

  // normal logic
  if (!shift.isWorking)
    return (
      <Badge
        title="Weekly Off"
        className={`bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-500/20 hover:bg-gray-500/20 ${baseClasses}`}
      >
        Off
      </Badge>
    );

  if (shift.isWorking)
    return (
      <Badge
        title={`Shift: ${name}`}
        className={`bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 ${baseClasses}`}
      >
        {name}
      </Badge>
    );

  return null;
};

  const WeekdaySelector = ({ day, config, onChange, isWeekend = false }) => {
    const isCustom = config.type === "custom";

    return (
      <div
        className={`p-4 rounded-xl border 
    ${isWeekend ? "border-border" : "border-border"}
    bg-card text-card-foreground
    flex flex-col gap-3 transition-all hover:border-muted-foreground/40`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm shadow-sm
          ${
            config.isWorking
              ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20"
              : "bg-muted text-muted-foreground border border-border"
          }`}
            >
              {day.charAt(0)}
            </div>

            <span className="font-semibold text-foreground">{day}</span>
          </div>

          {!isWeekend && (
            <Switch
              checked={config.isWorking}
              onCheckedChange={(val) =>
                onChange(day, {
                  ...config,
                  isWorking: val,
                  type: val ? "full" : "off",
                })
              }
            />
          )}

          {isWeekend && (
            <div className="max-w-[200px] w-full">
              <Select
                value={config.type}
                onValueChange={(value) =>
                  onChange(day, {
                    ...config,
                    type: value,
                    isWorking: value !== "off",
                  })
                }
              >
                <SelectTrigger className="text-sm h-9 px-3 w-full bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value="full">Full Working</SelectItem>
                  <SelectItem value="off">Weekly Off</SelectItem>
                  <SelectItem value="custom">Custom / Alternate</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {isCustom && (
          <div className="pl-12 animate-in slide-in-from-top-1">
            <p className="text-xs font-bold text-muted-foreground mb-2 uppercase">
              Select working {day}s:
            </p>

            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((week) => (
                <label
                  key={week}
                  className={`w-8 h-8 rounded-lg border flex items-center justify-center text-xs font-medium cursor-pointer transition-all
              ${
                config.specificWeeks.includes(week)
                  ? "bg-primary text-primary-foreground border-primary shadow-md shadow-primary/20"
                  : "bg-background text-muted-foreground border-border hover:border-muted-foreground/40"
              }`}
                >
                  <Input
                    type="checkbox"
                    className="hidden"
                    checked={config.specificWeeks.includes(week)}
                    onChange={(e) => {
                      const newWeeks = e.target.checked
                        ? [...config.specificWeeks, week]
                        : config.specificWeeks.filter((w) => w !== week);
                      onChange(day, { ...config, specificWeeks: newWeeks });
                    }}
                  />
                  {week}
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const RadioGroup = ({ value, onValueChange, className = "", children }) => {
    return (
      <div className={className}>
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, {
              selectedValue: value,
              onChange: onValueChange,
            });
          }
          return child;
        })}
      </div>
    );
  };

  const RadioOptionWrapper = ({ value, selectedValue, onChange, children }) => {
    const isSelected = value === selectedValue;

    return (
      <button
        type="button"
        onClick={() => onChange(value)}
        className="w-full text-left"
        aria-pressed={isSelected}
      >
        <div
          data-state={isSelected ? "checked" : "unchecked"}
          className="
          group
          rounded-xl
          outline-none
          focus-visible:ring-2 focus-visible:ring-primary/30
          focus-visible:ring-offset-2 focus-visible:ring-offset-background
        "
        >
          {children}
        </div>
      </button>
    );
  };

  const handleInputChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleDayChange = (day, newConfig) => {
    setFormData((prev) => ({
      ...prev,
      schedule: { ...prev.schedule, [day]: newConfig },
    }));
  };

  const updateOtRequest = (req) => {
    try {
      console.log(req);
      setOtViewOnly(req.status !== "pending" || !canEditOT);
      setShowAdminOTDialog(true);
      setAdminOTForm({
        ...adminOTForm,
        employee_id: req.employee_id,
        employee_name: req.employee_name,
        date: req.date,
        from_time: req.from_time,
        to_time: req.to_time,
        project: req.project,
        notes: req.notes,
        applied_date: req.applied_date,
        approved_by: req.approved_by,
        approved_date: req.approved_date,
        company_id: req.company_id,
        ot_hours: req.ot_hours,
        rejected_by: req.rejected_by,
        rejected_date: req.rejected_date,
        rejection_reason: req.rejection_reason,
        status: req.status,
        _id: req._id,
      });
    } catch (error) {
      console.log(error);
      toast.error("");
    }
  };

  const handleAdminOTSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit("overtime_management", true)) return;
    try {
      await updateOvertimeStatus(adminOTForm);
      toast.success("Successfully updating OT requests");
      setShowAdminOTDialog(false);
      canManageOvertime && await fetchovertimeRequests();
      await fetchRotationalShifts();
    } catch (error) {
      toast.error("Error updating OT requests");
    } finally {
    }
  };

  const OvertimeListItem = ({ request, onReview }) => {
    // Status Badge Helpers
    const getStatusStyle = (status) => {
      switch (status) {
        case "approved":
          return "bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20 hover:border-emerald-500/30";
        case "rejected":
          return "bg-red-500/10 text-red-400 border-red-500/20 hover:bg-red-500/20 hover:border-red-500/30";
        default:
          return "bg-amber-500/10 text-amber-400 border-amber-500/20 hover:bg-amber-500/20 hover:border-amber-500/30";
      }
    };

    const getStatusLabel = (status) => {
      switch (status) {
        case "approved":
          return "Approved";
        case "rejected":
          return "Rejected";
        default:
          return "Pending Review";
      }
    };

    return (
      <TableRow
        className="border-b border-border hover:bg-muted/50 transition-colors cursor-pointer"
        onClick={() => onReview(request)}
      >
        <TableCell className="py-3 px-4 font-medium text-foreground min-w-[200px]">
          <div className="flex items-center gap-4">
            {/* <div className="h-10 w-10 rounded-full bg-primary/10 dark:bg-slate-800 flex items-center justify-center text-slate-400 border border-slate-700">
                <User size={18} />
              </div> */}
            <div>
              <h4 className="font-semibold text-foreground text-sm">
                {request.employee_name}
              </h4>
              <p className="text-xs ">{request.employee_id}</p>
            </div>
          </div>
        </TableCell>

        <TableCell className="py-3 px-4 text-sm text-foreground">
          <div className="flex items-center justify-center gap-1.5">
            <Calendar size={14} className="" />
            {new Date(request.date).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </div>
        </TableCell>

        <TableCell className="py-3 px-4 text-sm text-foreground">
          <div className="flex items-center justify-center gap-1.5">
            <Briefcase size={14} className="" />
            <span className="truncate max-w-[180px] text-center">
              {request.project || "-"}
            </span>
          </div>
        </TableCell>

        <TableCell className="py-3 px-4 text-sm text-foreground">
          <div className="flex items-center justify-center gap-2">
            <Clock size={14} className="" />
            <span className="font-medium ">{request.ot_hours}h</span>
          </div>
        </TableCell>

        <TableCell className="py-3 px-4">
          <div className="flex justify-center">
            <Badge
              className={`border px-3 py-1.5 text-xs ${getStatusStyle(
                request.status,
              )}`}
            >
              {getStatusLabel(request.status)}
            </Badge>
          </div>
        </TableCell>

        <TableCell className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all flex items-center group/btn"
              onClick={() => onReview(request)}
            >
              <Eye className="w-3.5 h-3.5 mr-2 opacity-60 group-hover/btn:text-[#3194a0] group-hover/btn:opacity-100 transition-all" />
              <span className="group-hover/btn:text-[#3194a0] transition-colors">
                View
              </span>
              <ChevronRight className="w-3 h-3 ml-1 opacity-0 -translate-x-1 group-hover/btn:opacity-100 group-hover/btn:translate-x-0 group-hover/btn:text-[#3194a0] transition-all" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  };
 
  function getTimeDifference(start, end) {
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);

    const startMinutes = sh * 60 + sm;
    const endMinutes = eh * 60 + em;

    if (endMinutes < startMinutes) {
      return 24 * 60 - startMinutes + endMinutes;
    }

    return endMinutes - startMinutes; // returns difference in minutes
  }

  const handleOTSubmit = async (e) => {
    e.preventDefault();
    setOtTimeError("");

    // ✅ OT can be logged for any date

    const today = format(new Date(), "yyyy-MM-dd");

    // Validate that end time is not in the future (ONLY if selected date is today)

    const now = new Date();

    const currentHour = now.getHours();

    const currentMinute = now.getMinutes();

    const [toHourCheck, toMinCheck] = otForm.to_time.split(":").map(Number);

    if (otForm.date === today) {
      const endTimeInMinutes = toHourCheck * 60 + toMinCheck;

      const currentTimeInMinutes = currentHour * 60 + currentMinute;

      if (endTimeInMinutes > currentTimeInMinutes) {
        setOtTimeError(
          `You cannot log OT for future times. Current time is ${currentHour.toString().padStart(2, "0")}:${currentMinute.toString().padStart(2, "0")}, but you're trying to log until ${otForm.to_time}.`,
        );
        return;
      }
    }

    const parseTimeToMinutes = (t) => {
      const [h, m] = t.split(":").map(Number);

      return h * 60 + m;
    };

    const otFromMinutes = parseTimeToMinutes(otForm.from_time);

    const otToMinutes = parseTimeToMinutes(otForm.to_time);

    // ✅ sanity: end must be after start

    if (otToMinutes <= otFromMinutes) {
      setOtTimeError("To time must be after From time.");

      return;
    }

    if (otToMinutes - otFromMinutes < 30) {
      setOtTimeError("To time must be at least 30 minutes after From time.");

      return;
    }

    // ======================================================
    // ⭐ SHIFT OVERLAP VALIDATION
    // OT must be fully BEFORE shift start OR fully AFTER shift end
    // ======================================================
    const selectedEmployee = employees?.find(
      (emp) => String(emp._id) === String(otForm.employee_id),
    );

    if (!selectedEmployee?._id) {
      toast.error("Please select an employee.");

      return;
    }

    const shiftStart = selectedEmployee?.shift?.startTime; // e.g. "09:00"

    const shiftEnd = selectedEmployee?.shift?.endTime; // e.g. "18:30"

    if (shiftStart && shiftEnd) {
      const shiftStartMinutes = parseTimeToMinutes(shiftStart);

      const shiftEndMinutes = parseTimeToMinutes(shiftEnd);

      // ❌ overlap if OT starts before shift ends AND OT ends after shift starts

      const overlapsShift =
        otFromMinutes < shiftEndMinutes && otToMinutes > shiftStartMinutes;

      if (overlapsShift) {
        toast.error(
          `OT must not overlap shift time (${shiftStart} - ${shiftEnd}). ` +
            `Choose OT fully before shift start OR fully after shift end.`,
        );

        return;
      }
    } else if (shiftEnd) {
      // fallback: if only end time exists, enforce OT starts after shift end

      const shiftEndMinutes = parseTimeToMinutes(shiftEnd);

      if (otFromMinutes <= shiftEndMinutes) {
        toast.error(
          `OT Start Time (${otForm.from_time}) must be AFTER shift end time (${shiftEnd}).`,
        );
        return;
      }
    }

    // ======================================================
    // Calculate total OT Hours
    // ======================================================

    const totalMinutes = otToMinutes - otFromMinutes;

    const totalHours = totalMinutes / 60;

    otForm.employee_id = selectedEmployee._id;

    try {
      await createOTLog(otForm);

      toast.success("OT hours logged successfully!");
      setShowOTDialog(false);
      setOTForm({
        employee_id: "",
        date: format(new Date(), "yyyy-MM-dd"),
        from_time: "",
        to_time: "",
        project: "",
        notes: "",
      });

      fetchovertimeRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to log OT hours");
    }
  };

  function getTimeDiffHHMM(start, end) {
    const diff = getTimeDifference(start, end);
    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;

    return `${hours}h ${minutes}m`;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Shifts & Overtime Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage shift schedules and overtime policies
          </p>
        </div>
        {activeTab === "shifts" && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    className="gap-2"
                    variant="default"
                    disabled={
                      (!isPaidOnline && hasSingleShiftLimit) || !canCreateShift
                    }
                    onClick={() => {
                      if (!canCreate("shifts", true)) return;
                      if (hasSingleShiftLimit) {
                        navigate("/subscription", {
                          state: {
                            from: location.pathname,
                            reason: "feature_upgrade_required",
                            requiredFeature: "multi_shift_support",
                          },
                        });
                        return;
                      }

                      handleOpenAddShiftModal();
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    Add New Shift
                    {hasSingleShiftLimit && <Lock className="h-4 w-4" />}
                  </Button>
                </span>
              </TooltipTrigger>

              {hasSingleShiftLimit && (
                <TooltipContent>
                  <p className="font-semibold">
                    Multiple Shifts not available.
                  </p>
                  <p className="text-xs">
                    Upgrade your plan to add multiple shifts.
                  </p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        )}

        {activeTab === "overtime" && (
          <Dialog
            open={showOTDialog}
            onOpenChange={(open) => {
              if (!canCreate("overtime_management", true)) return;
              setShowOTDialog(open);
              setOtTimeError("");
              if (open) {
                // Reset form with today's date when dialog opens
                setOTForm({
                  date: format(new Date(), "yyyy-MM-dd"),
                  from_time: "",
                  to_time: "",
                  project: "",
                  notes: "",
                });
              }
            }}
          >
            <DialogTrigger asChild>
              <Button disabled={!canCreateOT}>
                <Clock className="w-4 h-4 mr-2" />
                Log OT
              </Button>
            </DialogTrigger>
            <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden border border-border/60 bg-background p-0 shadow-2xl sm:max-w-2xl">
              <DialogHeader className="border-b border-border px-6 py-5">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/20">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div className="space-y-1">
                    <DialogTitle className="text-xl font-semibold text-foreground">
                      Log Overtime Hours
                    </DialogTitle>
                    <DialogDescription className="text-sm text-muted-foreground">
                      Record employee overtime with the date, project, and time
                      window.
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>
              <form
                onSubmit={handleOTSubmit}
                className="flex flex-1 flex-col overflow-hidden"
              >
                <div className="flex-1 space-y-6 overflow-y-auto bg-muted/20 px-6 py-6">
                  <div className="grid gap-6 lg:grid-cols-1">
                    <div className="space-y-5 rounded-2xl border border-border bg-card p-5 shadow-sm">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-foreground">
                          Employee <span className="text-destructive">*</span>
                        </Label>
                        <Select
                          value={otForm.employee_id}
                          onValueChange={(v) =>
                            setOTForm({ ...otForm, employee_id: v })
                          }
                          disabled={!employees || employees.length === 0}
                        >
                          <SelectTrigger className="border-border bg-muted/40">
                            <SelectValue placeholder="Select employee" />
                          </SelectTrigger>
                          <SelectContent>
                            {employees.map((emp) => (
                              <SelectItem key={emp._id} value={emp._id}>
                                {emp.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-foreground">
                          Date
                        </Label>
                        <DatePickerInput
                          value={otForm.date}
                          onChange={(e) => setOTForm({ ...otForm, date: e })}
                          title="OT can only be logged for last & days"
                          className="border-border bg-muted/40"
                        />
                        <p className="text-xs text-muted-foreground">
                          OT can only be logged for today's date.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-foreground">
                          Project Name *
                        </Label>
                        <Input
                          type="text"
                          value={otForm.project}
                          onChange={(e) =>
                            setOTForm({ ...otForm, project: e.target.value })
                          }
                          placeholder="Enter project name"
                          className="border-border bg-muted/40 focus-visible:ring-primary"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                    <div className="mb-4">
                      <h3 className="text-sm font-semibold text-foreground">
                        Time Window
                      </h3>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Select the exact overtime range for this entry.
                      </p>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2 rounded-2xl border border-border bg-muted/30 p-4">
                        <Label className="text-sm font-medium text-foreground">
                          From Time
                        </Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Select
                            value={otForm.from_time.split(":")[0] || ""}
                            onValueChange={(value) => {
                              const minutes =
                                otForm.from_time.split(":")[1] || "00";
                              setOtTimeError("");
                              setOTForm({
                                ...otForm,
                                from_time: `${value}:${minutes}`,
                              });
                            }}
                          >
                            <SelectTrigger className="border-border bg-background">
                              <SelectValue placeholder="Hour" />
                            </SelectTrigger>
                            <SelectContent className="max-h-60">
                              {Array.from({ length: 24 }, (_, i) => (
                                <SelectItem
                                  key={i}
                                  value={i.toString().padStart(2, "0")}
                                >
                                  {i.toString().padStart(2, "0")}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select
                            value={otForm.from_time.split(":")[1] || ""}
                            onValueChange={(value) => {
                              const hour =
                                otForm.from_time.split(":")[0] || "00";
                              setOtTimeError("");
                              setOTForm({
                                ...otForm,
                                from_time: `${hour}:${value}`,
                              });
                            }}
                          >
                            <SelectTrigger className="border-border bg-background">
                              <SelectValue placeholder="Min" />
                            </SelectTrigger>
                            <SelectContent className="max-h-60">
                              {minuteOptions.map((minute) => (
                                <SelectItem key={minute} value={minute}>
                                  {minute}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2 rounded-2xl border border-border bg-muted/30 p-4">
                        <Label className="text-sm font-medium text-foreground">
                          To Time
                        </Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Select
                            value={otForm.to_time.split(":")[0] || ""}
                            onValueChange={(value) => {
                              const minutes =
                                otForm.to_time.split(":")[1] || "00";
                              setOtTimeError("");
                              setOTForm({
                                ...otForm,
                                to_time: `${value}:${minutes}`,
                              });
                            }}
                          >
                            <SelectTrigger className="border-border bg-background">
                              <SelectValue placeholder="Hour" />
                            </SelectTrigger>
                            <SelectContent className="max-h-60">
                              {Array.from({ length: 24 }, (_, i) => (
                                <SelectItem
                                  key={i}
                                  value={i.toString().padStart(2, "0")}
                                >
                                  {i.toString().padStart(2, "0")}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select
                            value={otForm.to_time.split(":")[1] || ""}
                            onValueChange={(value) => {
                              const hour = otForm.to_time.split(":")[0] || "00";
                              setOtTimeError("");
                              setOTForm({
                                ...otForm,
                                to_time: `${hour}:${value}`,
                              });
                            }}
                          >
                            <SelectTrigger className="border-border bg-background">
                              <SelectValue placeholder="Min" />
                            </SelectTrigger>
                            <SelectContent className="max-h-60">
                              {minuteOptions.map((minute) => (
                                <SelectItem key={minute} value={minute}>
                                  {minute}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                    {otTimeError && (
                      <p className="mt-4 rounded-xl border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm font-medium text-destructive">
                        {otTimeError}
                      </p>
                    )}
                  </div>

                  <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 to-background p-5 shadow-sm">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                      OT Rules
                    </p>
                    <ul className="list-disc space-y-2 pl-4 text-sm leading-6 text-foreground/90">
                      <li>To Time must always be after From Time.</li>
                      <li>Minimum OT duration is 30 minutes.</li>
                      <li>
                        On working days, OT is allowed only before shift start
                        or after shift end.
                      </li>
                      <li>
                        On weekends or holidays, OT can be logged anytime.
                      </li>
                      <li>Overlapping entries will be rejected.</li>
                    </ul>
                  </div>

                  <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-foreground">
                        Notes (Optional)
                      </Label>
                      <Textarea
                        value={otForm.notes}
                        onChange={(e) =>
                          setOTForm({ ...otForm, notes: e.target.value })
                        }
                        placeholder="Add a short summary of the overtime work completed"
                        className="min-h-[120px] resize-none border-border bg-muted/40 focus-visible:ring-primary"
                      />
                    </div>
                  </div>
                </div>

                <div className="shrink-0 border-t border-border bg-background/95 px-6 py-4 backdrop-blur">
                  <Button
                    type="submit"
                    className="h-11 w-full rounded-xl bg-primary shadow-lg shadow-primary/20 transition hover:bg-primary/90"
                  >
                    Log OT Hours
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex justify-start space-x-8 overflow-x-auto bg-transparent h-auto p-0 no-scrollbar">
            {canViewShift && (
              <TabsTrigger value="shifts">Shift Master</TabsTrigger>
            )}

            {canViewSchedules && (
              <TabsTrigger value="rotational">Rotational Planner</TabsTrigger>
            )}

            {canManageOvertime && canViewOT && (
              <TabsTrigger value="overtime">Overtime Requests</TabsTrigger>
            )}
        </TabsList>

        {canViewShift && (
          <TabsContent value="shifts" className="space-y-4 mt-6">
            <div className="grid gap-4">
              {shiftMasterLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <Card key={`shift-master-skeleton-${i}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <Skeleton className="h-12 w-12 rounded-xl" />
                          <div className="space-y-2">
                            <Skeleton className="h-6 w-44" />
                            <Skeleton className="h-4 w-28" />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Skeleton className="h-10 w-10 rounded-md" />
                          <Skeleton className="h-10 w-10 rounded-md" />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-border">
                        {Array.from({ length: 3 }).map((__, statIdx) => (
                          <div
                            key={`shift-master-stat-skeleton-${i}-${statIdx}`}
                          >
                            <Skeleton className="h-3 w-24" />
                            <Skeleton className="h-4 w-20 mt-2" />
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (shiftData?.length ?? 0) === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
                    <Clock className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">
                    No Shifts Added
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Create your first shift to get started.
                  </p>
                </div>
              ) : (
                shiftData.map((shift, index) => (
                  <Card
                    key={shift._id || index}
                    className="hover:shadow-lg transition-shadow"
                  >
                    <CardContent className="p-6">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Clock className="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold text-foreground">
                              {shift.name}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1">
                              {shift.startTime} - {shift.endTime}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            disabled={!canEditShift}
                            onClick={() => {
                              if (!canEdit("shifts", true)) return;
                              handleEditShift(shift);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            disabled={!canDeleteShift}
                            onClick={() => {
                              if (!canDelete("shifts", true)) return;
                              handleDeleteShift(shift);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-border">
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Break Duration
                          </p>
                          <p className="text-sm font-semibold text-foreground mt-1">
                            {shift.breakDuration} mins
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Grace Period
                          </p>
                          <p className="text-sm font-semibold text-foreground mt-1">
                            {shift.graceTime} mins
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Work Hours
                          </p>
                          <p className="text-sm font-semibold text-foreground mt-1">
                            {getTimeDiffHHMM(shift.startTime, shift.endTime)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        )}

        {/* Rotational Planner */}
        {canViewSchedules && (
          <TabsContent value="rotational" className="space-y-4 mt-6">
            <Card>
              <CardHeader className="space-y-4">
                {/* Top Row: Title + Search */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <CardTitle className="text-lg font-semibold">
                    Weekly Shift Planner
                  </CardTitle>

                  <div className="w-full md:w-80">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search by employee names"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>

                <CardDescription>
                  Manage rotational shift schedules for employees
                </CardDescription>
              </CardHeader>

              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                          Employee
                        </th>
                        <th className="text-center py-3 px-4 font-medium text-sm text-muted-foreground">
                          Mon
                        </th>
                        <th className="text-center py-3 px-4 font-medium text-sm text-muted-foreground">
                          Tue
                        </th>
                        <th className="text-center py-3 px-4 font-medium text-sm text-muted-foreground">
                          Wed
                        </th>
                        <th className="text-center py-3 px-4 font-medium text-sm text-muted-foreground">
                          Thu
                        </th>
                        <th className="text-center py-3 px-4 font-medium text-sm text-muted-foreground">
                          Fri
                        </th>
                        <th className="text-center py-3 px-4 font-medium text-sm text-muted-foreground">
                          Sat
                        </th>
                        <th className="text-center py-3 px-4 font-medium text-sm text-muted-foreground">
                          Sun
                        </th>
                        <th className="text-center py-3 px-4 font-medium text-sm text-muted-foreground">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        Array.from({ length: 6 }).map((_, idx) => (
                          <tr
                            key={`rotational-skeleton-${idx}`}
                            className="border-b border-border"
                          >
                            <td className="py-3 px-4">
                              <Skeleton className="h-6 w-40" />
                            </td>
                            {Array.from({ length: 7 }).map((__, dayIdx) => (
                              <td
                                key={`rotational-skeleton-${idx}-${dayIdx}`}
                                className="py-3 px-4 text-center"
                              >
                                <Skeleton className="h-6 w-20 mx-auto" />
                              </td>
                            ))}
                            <td className="py-3 px-4 text-center">
                              <Skeleton className="h-8 w-8 mx-auto" />
                            </td>
                          </tr>
                        ))
                      ) : paginatedRotationalRequests.length > 0 ? (
                        paginatedRotationalRequests.map((employee, index) => (
                          <tr
                            key={employee._id || index}
                            className="border-b border-border hover:bg-muted/50 transition-colors"
                          >
                            <td className="py-3 px-4 font-medium text-foreground">
                              {employee.name}
                            </td>

                            <td className="py-3 px-4 text-center">
                              {getShiftBadge(
                                employee.shift?.schedule?.Monday,
                                employee.shift?.name,
                              )}
                            </td>

                            <td className="py-3 px-4 text-center">
                              {getShiftBadge(
                                employee.shift?.schedule?.Tuesday,
                                employee.shift?.name,
                              )}
                            </td>

                            <td className="py-3 px-4 text-center">
                              {getShiftBadge(
                                employee.shift?.schedule?.Wednesday,
                                employee.shift?.name,
                              )}
                            </td>

                            <td className="py-3 px-4 text-center">
                              {getShiftBadge(
                                employee.shift?.schedule?.Thursday,
                                employee.shift?.name,
                              )}
                            </td>

                            <td className="py-3 px-4 text-center">
                              {getShiftBadge(
                                employee.shift?.schedule?.Friday,
                                employee.shift?.name,
                              )}
                            </td>

                            <td className="py-3 px-4 text-center">
                              {getShiftBadge(
                                employee.shift?.schedule?.Saturday,
                                employee.shift?.name,
                              )}
                            </td>

                            <td className="py-3 px-4 text-center">
                              {getShiftBadge(
                                employee.shift?.schedule?.Sunday,
                                employee.shift?.name,
                              )}
                            </td>

                            <td className="py-3 px-4 text-center">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditSchedule(employee)}
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={9}
                            className="py-8 text-center text-muted-foreground"
                          >
                            No shift schedules found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-center gap-3 pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={weeklyPage === 1}
                    onClick={() => setWeeklyPage((prev) => prev - 1)}
                  >
                    Previous
                  </Button>

                  <span className="text-sm text-muted-foreground">
                    Page {weeklyPage} of {weeklyTotalPages}
                  </span>

                  <Button
                    variant="outline"
                    size="sm"
                    disabled={weeklyPage === weeklyTotalPages}
                    onClick={() => setWeeklyPage((prev) => prev + 1)}
                  >
                    Next
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Overtime Requests */}
        {canManageOvertime && canViewOT && (
          <TabsContent value="overtime" className="space-y-4 mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Overtime Summary (This Month)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 border border-border rounded-xl">
                    <p className="text-sm text-muted-foreground">
                      Total OT Hours
                    </p>
                    <p className="text-2xl font-bold text-primary mt-2">
                      {overtimeSummary.totalCombinedOTHOurs.toFixed(2)}h
                    </p>
                  </div>
                  <div className="p-4 border border-border rounded-xl">
                    <p className="text-sm text-muted-foreground">
                      Pending Approvals
                    </p>
                    <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400 mt-2">
                      {overtimeSummary.pendingOTApprovals}
                    </p>
                  </div>
                  <div className="p-4 border border-border rounded-xl">
                    <p className="text-sm text-muted-foreground">
                      Employees with OT
                    </p>
                    <p className="text-2xl font-bold text-foreground mt-2">
                      {overtimeSummary.employeewithOT}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Overtime Requests</CardTitle>
                <CardDescription>
                  Review and approve overtime work hours
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50 border-b border-border hover:bg-muted/50">
                        <TableHead className="py-3 px-4 text-start font-semibold text-foreground">
                          Employee
                        </TableHead>
                        <TableHead className="py-3 px-4 text-center font-semibold text-foreground">
                          Date
                        </TableHead>
                        <TableHead className="py-3 px-4 text-center font-semibold text-foreground">
                          Project
                        </TableHead>
                        <TableHead className="py-3 px-4 text-center font-semibold text-foreground">
                          Duration
                        </TableHead>
                        <TableHead className="py-3 px-4 text-center font-semibold text-foreground">
                          Status
                        </TableHead>
                        <TableHead className="py-3 px-4 text-center font-semibold text-foreground">
                          Action
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedRequests.length > 0 ? (
                        paginatedRequests.map((request) => (
                          <OvertimeListItem
                            key={request._id || request.id}
                            request={request}
                            onReview={updateOtRequest}
                          />
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            className="py-8 text-center text-muted-foreground"
                          >
                            No overtime requests found.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
                {/* Pagination Controls */}
                <div className="flex items-center justify-end sm:justify-between gap-3 mt-4">
                  <PaginationPageSize value={itemsPerPage} onValueChange={handlePageSizeChange} />
                  <div className="flex items-center justify-center gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => p - 1)}
                    >
                      Previous
                    </Button>

                    <span className="text-sm text-muted-foreground">
                      Page {currentPage} of {totalPages}
                    </span>

                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Edit Schedule Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Edit Weekly Schedule</DialogTitle>
            <DialogDescription>
              Update shift assignments for {selectedEmployee}
            </DialogDescription>
          </DialogHeader>

          {editedSchedule && (
            <div className="space-y-4 overflow-y-auto py-4 pr-1">
              <div className="grid grid-cols-1 gap-4">
                <Select
                  value={editedSchedule.shift?._id}
                  onValueChange={(shiftId) => {
                    const selectedShift = shiftData.find(
                      (s) => s._id === shiftId,
                    );
                    setEditedSchedule((prev) => ({
                      ...prev,
                      shift: selectedShift, // store full object
                      activeShift: shiftId, // also store id if needed
                    }));
                  }}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select a shift" />
                  </SelectTrigger>

                  <SelectContent>
                    {(shiftData?.length ?? 0) === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <h3 className="text-lg font-semibold text-foreground">
                          No Shifts Added
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Create your first shift to get started.
                        </p>
                      </div>
                    ) : (
                      shiftData.map((shift) => (
                        <SelectItem key={shift._id} value={shift._id}>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-orange-500/10 text-orange-600 border-orange-500/20">
                              {shift.name}
                            </Badge>

                            {shift._id !== "off" ? (
                              <span className="text-sm text-muted-foreground">
                                {shift.startTime} - {shift.endTime}
                              </span>
                            ) : (
                              <span className="text-sm text-muted-foreground">
                                Rest day
                              </span>
                            )}
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-border">
                <Button
                  variant="outline"
                  onClick={() => setEditDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleShiftChange}>Save Changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Shift Timings Dialog */}
      <Dialog open={editShiftDialogOpen} onOpenChange={setEditShiftDialogOpen}>
        <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editedShift ? "Edit" : "Add"} {selectedShift?.name} Shift Timings
            </DialogTitle>
            <DialogDescription>
              Customize start time, end time, break duration, and grace period
              for this shift
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 overflow-y-auto pr-1">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <Label>Schedule Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                  }}
                  placeholder="e.g. General Shift, Support Team, Retail Roster"
                />
              </div>
              <div>
                <Label>Shift Start Time</Label>
                <Input
                  type="time"
                  value={formData.startTime}
                  onChange={(e) =>
                    setFormData({ ...formData, startTime: e.target.value })
                  }
                  icon={<Sun size={16} />}
                />
              </div>
              <div>
                <Label>Shift End Time</Label>
                <Input
                  type="time"
                  value={formData.endTime}
                  onChange={(e) =>
                    setFormData({ ...formData, endTime: e.target.value })
                  }
                  icon={<Moon size={16} />}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label>Break Duration (In minutes) </Label>
                <Input
                  type="number"
                  value={formData.breakDuration}
                  onChange={handleInputChange("breakDuration")}
                  icon={<Sun size={16} />}
                />
              </div>
              <div>
                <Label>Break Start At</Label>
                <Input
                  type="time"
                  value={formData.breakStartsAt}
                  onChange={handleInputChange("breakStartsAt")}
                  icon={<Moon size={16} />}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label>Grace Time (In minutes) </Label>
                <Input
                  type="text"
                  value={formData.graceTime}
                  onChange={handleInputChange("graceTime")}
                />
              </div>
            </div>
            <hr className="border-slate-100" />

            {/* Weekdays */}
            <div>
              <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
                <CalendarDays size={16} className="text-[#3194A0]" /> Weekdays
                (Mon-Fri)
              </h4>
              <div className="grid grid-cols-1 gap-2">
                {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].map(
                  (day) => (
                    <WeekdaySelector
                      key={day}
                      day={day}
                      config={formData.schedule[day]}
                      onChange={handleDayChange}
                    />
                  ),
                )}
              </div>
            </div>

            {/* Weekends */}
            <div>
              <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
                <Palmtree size={16} className="text-[#3194A0]" /> Weekends
                (Sat-Sun)
              </h4>
              <div className="grid grid-cols-1 gap-2">
                {["Saturday", "Sunday"].map((day) => (
                  <WeekdaySelector
                    key={day}
                    day={day}
                    config={formData.schedule[day]}
                    onChange={handleDayChange}
                    isWeekend={true}
                  />
                ))}
              </div>
            </div>

            <div className="pt-6 flex gap-3 border-t border-slate-100 mt-4">
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => setEditShiftDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                className="w-full"
                onClick={() => {
                  handleSaveShift();
                }}
              >
                Save Schedule
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <RequestDetailsDialog
        open={showAdminOTDialog}
        onOpenChange={setShowAdminOTDialog}
        title={
          <>
            {otViewOnly ? (
              <Eye className="h-6 w-6 text-blue-500" />
            ) : (
              <Clock className="h-6 w-6 text-primary" />
            )}
            {otViewOnly ? "Overtime Request Details" : "Review Overtime Request"}
          </>
        }
        description={
          otViewOnly
            ? "Review the details of this overtime entry."
            : "Verify details and approve or reject this overtime entry."
        }
        fields={[
          {
            label: "Employee",
            value: adminOTForm.employee_name,
            valueClassName: "font-semibold text-sm",
          },
          {
            label: "Date",
            value: adminOTForm.date
              ? new Date(adminOTForm.date).toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
              : "N/A",
          },
          {
            label: "Project",
            value: adminOTForm.project || "—",
          },
          {
            label: "Duration",
            value: `${String(adminOTForm.from_time).padStart(2, "0")} – ${String(
              adminOTForm.to_time,
            ).padStart(2, "0")}`,
          },
          adminOTForm.notes && {
            label: "Notes",
            value: adminOTForm.notes,
            fullWidth: true,
            valueClassName: "text-sm italic text-foreground/80",
          },
          otViewOnly && {
            label: "Status",
            value:
              adminOTForm.status === "approved"
                ? "APPROVED"
                : adminOTForm.status === "rejected"
                  ? "REJECTED"
                  : "PENDING",
            valueClassName: `font-semibold text-sm ${
              adminOTForm.status === "approved"
                ? "text-green-600"
                : adminOTForm.status === "rejected"
                  ? "text-red-600"
                  : "text-yellow-600"
            }`,
          },
          otViewOnly &&
            adminOTForm.status === "rejected" &&
            adminOTForm.rejection_reason && {
              label: "Rejection Reason",
              value: adminOTForm.rejection_reason,
              fullWidth: true,
              valueClassName: "text-sm italic text-foreground/80",
            },
        ]}
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowAdminOTDialog(false)}
            >
              {otViewOnly ? "Close" : "Cancel"}
            </Button>

            {!otViewOnly && (
              <Button
                type="button"
                onClick={handleAdminOTSubmit}
                disabled={!adminOTForm.status}
                className={`w-full sm:w-auto font-semibold ${
                  adminOTForm.status === "approved"
                    ? "bg-emerald-600 hover:bg-emerald-700"
                    : adminOTForm.status === "rejected"
                      ? "bg-red-600 hover:bg-red-700"
                      : "bg-slate-600 hover:bg-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600"
                }`}
              >
                Confirm Decision
              </Button>
            )}
          </>
        }
      >
        {/* ================= DECISION (review mode) ================= */}
        {!otViewOnly && (
          <div className="space-y-4 pt-2">
            <Label className="text-base font-semibold text-foreground">
              Decision
            </Label>

            <RadioGroup
              value={adminOTForm.status}
              onValueChange={(value) =>
                setAdminOTForm({ ...adminOTForm, status: value })
              }
              className="grid grid-cols-2 gap-5"
            >
                  {/* APPROVE */}
                  <RadioOptionWrapper value="approved">
                    <div
                      className={`group flex flex-col items-center gap-3 rounded-2xl border-2 p-6 cursor-pointer transition-all duration-200
                ${
                  adminOTForm.status === "approved"
                    ? "border-emerald-500 bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-950/50 dark:to-emerald-900/30 shadow-lg shadow-emerald-500/20 dark:shadow-emerald-500/10"
                    : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 hover:border-emerald-300 dark:hover:border-emerald-700 hover:shadow-md"
                }`}
                    >
                      <div
                        className={`rounded-full p-2 transition-colors ${
                          adminOTForm.status === "approved"
                            ? "bg-emerald-500"
                            : "bg-slate-100 dark:bg-slate-700 group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/50"
                        }`}
                      >
                        <CheckCircle2
                          className={`h-6 w-6 transition-colors ${
                            adminOTForm.status === "approved"
                              ? "text-white"
                              : "text-slate-400 dark:text-slate-500 group-hover:text-emerald-600 dark:group-hover:text-emerald-400"
                          }`}
                        />
                      </div>
                      <span
                        className={`font-semibold text-base ${
                          adminOTForm.status === "approved"
                            ? "text-emerald-700 dark:text-emerald-400"
                            : "text-slate-700 dark:text-slate-300"
                        }`}
                      >
                        Approve
                      </span>
                      <span className="text-xs text-center text-slate-500 dark:text-slate-400">
                        OT will be counted in payroll
                      </span>
                    </div>
                  </RadioOptionWrapper>

                  {/* REJECT */}
                  <RadioOptionWrapper value="rejected">
                    <div
                      className={`group flex flex-col items-center gap-3 rounded-2xl border-2 p-6 cursor-pointer transition-all duration-200
                ${
                  adminOTForm.status === "rejected"
                    ? "border-red-500 bg-gradient-to-br from-red-50 to-red-100/50 dark:from-red-950/50 dark:to-red-900/30 shadow-lg shadow-red-500/20 dark:shadow-red-500/10"
                    : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 hover:border-red-300 dark:hover:border-red-700 hover:shadow-md"
                }`}
                    >
                      <div
                        className={`rounded-full p-2 transition-colors ${
                          adminOTForm.status === "rejected"
                            ? "bg-red-500"
                            : "bg-slate-100 dark:bg-slate-700 group-hover:bg-red-100 dark:group-hover:bg-red-900/50"
                        }`}
                      >
                        <XCircle
                          className={`h-6 w-6 transition-colors ${
                            adminOTForm.status === "rejected"
                              ? "text-white"
                              : "text-slate-400 dark:text-slate-500 group-hover:text-red-600 dark:group-hover:text-red-400"
                          }`}
                        />
                      </div>
                      <span
                        className={`font-semibold text-base ${
                          adminOTForm.status === "rejected"
                            ? "text-red-700 dark:text-red-400"
                            : "text-slate-700 dark:text-slate-300"
                        }`}
                      >
                        Reject
                      </span>
                      <span className="text-xs text-center text-slate-500 dark:text-slate-400">
                        OT will not be counted
                      </span>
                    </div>
                  </RadioOptionWrapper>
                </RadioGroup>
              </div>
              )}

              {/* ================= REJECTION REASON ================= */}
              {!otViewOnly && adminOTForm.status === "rejected" && (
                <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
                  <Label
                    htmlFor="reason"
                    className="font-semibold text-slate-900 dark:text-slate-100"
                  >
                    Rejection Reason{" "}
                    <span className="text-red-500 dark:text-red-400">*</span>
                  </Label>

                  <Textarea
                    id="reason"
                    value={adminOTForm.rejection_reason}
                    onChange={(e) =>
                      setAdminOTForm({
                        ...adminOTForm,
                        rejection_reason: e.target.value,
                      })
                    }
                    placeholder="Clearly explain why this overtime request is being rejected…"
                    className="resize-none border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus-visible:ring-2 focus-visible:ring-red-500/20 focus-visible:border-red-500 dark:focus-visible:border-red-600 rounded-xl"
                    rows={3}
                  />
                </div>
              )}

      </RequestDetailsDialog>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={confirmData.title}
        message={confirmData.message}
        onConfirm={confirmDeleteShift}
      />
    </div>
  );
};

export default Shifts;
